import { useEffect, useState, useRef } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faCirclePlus, faVolumeHigh } from '@fortawesome/free-solid-svg-icons';
import { Alert } from '@/modules/market-signals/model/social-media';
import ColorSelectorMenu from './color-selector-menu';

interface AlertManagerProps {
  show: boolean;
  alerts: Alert[];
  onClose: () => void;
  onChangeAlerts: (alerts: Alert[]) => void;
  smartMatching: boolean;
  setSmartMatching: (value: boolean) => void;
}

export default function AlertManager({
  show,
  alerts,
  onClose,
  onChangeAlerts,
  smartMatching,
  setSmartMatching,
}: AlertManagerProps) {
  const [localAlerts, setLocalAlerts] = useState<Alert[]>(alerts);
  const [newWord, setNewWord] = useState('');
  const [newColor, setNewColor] = useState('#ff4d4f');
  const colorMenuRef = useRef<HTMLDivElement | null>(null);
  const [colorMenuIndex, setColorMenuIndex] = useState<number | null>(null);

  const presetColors = ['#ff4d4f', '#faad14', '#52c41a', '#1890ff', '#722ed1', '#13c2c2'];
  const soundOptions = [
    { label: 'No Sound', value: '' },
    { label: 'Newsflash', value: 'newsflash' },
    { label: 'Chord', value: 'chord' },
    { label: 'Bell', value: 'bell' },
    { label: 'Sharp', value: 'sharp' },
    { label: 'Angry Beep', value: 'angry_beep' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorMenuRef.current && !colorMenuRef.current.contains(event.target as Node)) {
        setColorMenuIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (show) setLocalAlerts(alerts);
  }, [show, alerts]);

  const handleAdd = () => {
    const word = newWord.trim();
    if (!word || localAlerts.some(a => a.word.toLowerCase() === word.toLowerCase())) return;

    setLocalAlerts([...localAlerts, { word, color: newColor }]);
    setNewWord('');
  };

  const updateAlert = (index: number, changes: Partial<Alert>) => {
    setLocalAlerts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...changes };
      return updated;
    });
  };

  const playSound = (sound?: string) => {
    if (sound) new Audio(`/sounds/${sound}.mp3`).play().catch(() => {});
  };

  return (
    <Modal show={show} onHide={onClose} style={{ marginTop: '10vh' }}>
      <Modal.Header closeButton>
        <Modal.Title>Manage Alerts</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form
          onSubmit={e => {
            e.preventDefault();
            handleAdd();
          }}
        >
          <div className="input-group mb-3 align-items-center gap-1">
            <Form.Control
              placeholder="Enter alert phrase"
              value={newWord}
              onChange={e => setNewWord(e.target.value)}
            />
            <ColorSelectorMenu
              currentColor={newColor}
              onColorChange={setNewColor}
              menuIndex={colorMenuIndex}
              thisIndex={-1}
              setMenuIndex={setColorMenuIndex}
              menuRef={colorMenuRef}
              presetColors={presetColors}
              size={44}
            />
            <Button type="submit" variant="secondary" style={{ height: 44 }}>
              <FontAwesomeIcon icon={faCirclePlus} />
            </Button>
          </div>
        </Form>

        {localAlerts.length === 0 && <p className="text-muted">No alert phrases added.</p>}
        <Form.Check
          type="checkbox"
          id="smart-matching-toggle"
          label={
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.0 }}>
              <span>Alert Smart Matching (e.g. bonds = bond, sold = sell)</span>
            </div>
          }
          checked={smartMatching}
          onChange={e => setSmartMatching(e.target.checked)}
          style={{ alignSelf: 'center', marginBottom: '1rem' }}
        />

        {localAlerts.map((alert, index) => (
          <div key={index} className="d-flex align-items-center mb-1 gap-2">
            <Button
              variant="link"
              title="Delete Alert"
              className="text-danger p-0 mb-1"
              size="lg"
              onClick={() => setLocalAlerts(prev => prev.filter((_, i) => i !== index))}
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </Button>

            <ColorSelectorMenu
              currentColor={alert.color}
              onColorChange={color => updateAlert(index, { color })}
              menuIndex={colorMenuIndex}
              thisIndex={index}
              setMenuIndex={setColorMenuIndex}
              menuRef={colorMenuRef}
              presetColors={presetColors}
            />

            <Form.Control
              value={alert.word}
              onChange={e => updateAlert(index, { word: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onChangeAlerts(localAlerts);
                  onClose();
                }
              }}
              style={{ minWidth: 0 }}
              size="sm"
            />

            <Form.Select
              value={alert.sound || ''}
              onChange={e => {
                const selectedSound = e.target.value || undefined;
                updateAlert(index, { sound: selectedSound });

                if (
                  selectedSound &&
                  'Notification' in window &&
                  Notification.permission === 'default'
                ) {
                  Notification.requestPermission().then(result => {
                    if (result === 'granted') {
                      new Notification('AF Social - Notifications enabled', {
                        body: 'You’ll now get desktop alerts when an alert with a sound is triggered.',
                      });
                    }
                  });
                }
              }}
              style={{ width: 110, flexShrink: 0 }}
              size="sm"
              title="Select sound"
            >
              {soundOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>

            <Button
              variant="link"
              size="sm"
              className="p-0 mb-1"
              title="Preview sound"
              onClick={() => playSound(alert.sound)}
              style={{ width: 28 }}
            >
              <FontAwesomeIcon icon={faVolumeHigh} />
            </Button>
          </div>
        ))}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={() => {
            const filtered = localAlerts.filter(alert => alert.word.trim() !== '');
            onChangeAlerts(filtered);
            onClose();
          }}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
